import { Component } from '@theme/component';

/**
 * Enhances a native testimonial swipe rail without autoplay.
 * @extends {Component<{rail: HTMLUListElement, cards: HTMLLIElement[], controls: HTMLElement, previous: HTMLButtonElement, next: HTMLButtonElement, dots: HTMLButtonElement[]}>}
 */
class SlTestimonials extends Component {
  #controller;
  #observer;
  #frame;
  #index = 0;
  /** Stillwell: slide a Next/Previous/dot asked for, while the smooth scroll is on its way (-1 when none) */
  #target = -1;
  #targetTimer;
  requiredRefs = ['rail'];

  connectedCallback() { super.connectedCallback(); this.#setup(); }
  updatedCallback() { super.updatedCallback(); this.#cleanup(); this.#setup(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#cleanup(); }

  #setup() {
    this.#controller = new AbortController();
    this.refs.rail.addEventListener('scroll', this.#schedule, { signal: this.#controller.signal, passive: true });
    // A finger or the wheel on the rail takes over from a Next/Previous scroll; the end of a scroll settles the index
    for (const type of ['pointerdown', 'touchstart', 'wheel']) {
      this.refs.rail.addEventListener(type, this.#release, { signal: this.#controller.signal, passive: true });
    }
    this.refs.rail.addEventListener('scrollend', this.#release, { signal: this.#controller.signal });
    // Stillwell: the rail is measured when its size is known (the observer reports it after layout), not while the
    // page is still being parsed: measuring the rail and every card at connect time forced a layout of the whole
    // product page. Until then the first card is the active one (the rail starts at the beginning).
    this.#observer = new ResizeObserver(() => this.#paint());
    this.#observer.observe(this.refs.rail);
    if (this.refs.controls) this.refs.controls.hidden = false;
    this.setAttribute('data-enhanced', '');
    this.#mark();
  }

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => { this.#frame = undefined; this.#paint(); });
  };

  #release = () => {
    if (this.#target < 0) return;
    this.#target = -1;
    clearTimeout(this.#targetTimer);
    this.#schedule();
  };

  #paint() {
    const cards = this.refs.cards ?? [];
    const rail = this.refs.rail.getBoundingClientRect();
    const center = rail.left + rail.width / 2;
    let distance = Infinity;
    let nearest = this.#index;
    for (const [index, card] of cards.entries()) {
      const bounds = card.getBoundingClientRect();
      const delta = Math.abs(bounds.left + bounds.width / 2 - center);
      if (delta < distance) { distance = delta; nearest = index; }
    }
    // Stillwell: the in-between positions of a Next/Previous scroll do not move the index (fast taps on Next stopped
    // at slide 2 of 3: each tap counted from the slide passing by)
    if (this.#target >= 0 && nearest !== this.#target) return;
    this.#index = nearest;
    this.#mark();
  }

  #mark() {
    const cards = this.refs.cards ?? [];
    for (const [index, card] of cards.entries()) card.classList.toggle('is-active', index === this.#index);
    for (const [index, dot] of (this.refs.dots ?? []).entries()) {
      if (index === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
    if (this.refs.previous) this.refs.previous.disabled = this.#index === 0;
    if (this.refs.next) this.refs.next.disabled = this.#index === cards.length - 1;
  }

  #go(index) {
    const cards = this.refs.cards ?? [];
    if (!cards.length) return;
    index = Math.max(0, Math.min(cards.length - 1, index));
    this.#target = index;
    this.#index = index;
    clearTimeout(this.#targetTimer);
    this.#targetTimer = setTimeout(this.#release, 900);
    this.#mark();
    // Stillwell: only the rail scrolls. scrollIntoView({ block: 'nearest' }) also scrolled the page whenever the card
    // (about 580px tall on phones) was not wholly on screen: the arrows jumped 150-350px away from the finger, under the
    // buy bar, and a second tap opened the card's product link.
    const rail = this.refs.rail;
    const card = cards[index];
    if (!rail || !card) return;
    const railBox = rail.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();
    const left = rail.scrollLeft + (cardBox.left + cardBox.width / 2) - (railBox.left + rail.clientLeft + rail.clientWidth / 2);
    rail.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  /** Navigate to the previous testimonial. */
  previous() { this.#go(this.#index - 1); }
  /** Navigate to the next testimonial. */
  next() { this.#go(this.#index + 1); }
  /** @param {MouseEvent} event - A dot activated with pointer or keyboard. */
  select(event) {
    if (!(event.target instanceof Element)) return;
    const dot = event.target.closest('button[data-index]');
    if (!(dot instanceof HTMLButtonElement)) return;
    this.#go(Number(dot.dataset.index));
  }

  #cleanup() {
    clearTimeout(this.#targetTimer);
    this.#target = -1;
    this.#controller?.abort();
    this.#observer?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }
}

if (!customElements.get('sl-testimonials')) customElements.define('sl-testimonials', SlTestimonials);
